import { getSftpFieldsError, getSftpUsernameWarning, getSshPublicKeyError } from '@steerfi/shared';
import { IconAlertCircle } from '@tabler/icons-react';
import type React from 'react';
import { SshPublicKeysEditor } from '@/components/ssh-public-keys-editor';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface SftpAccessFieldsProps {
  /** Distinguishes element ids between the create and edit forms. */
  idPrefix: string;
  isSftp: boolean;
  onIsSftpChange: (value: boolean) => void;
  sftpUsername: string;
  onSftpUsernameChange: (value: string) => void;
  sshPublicKeys: string[];
  onSshPublicKeysChange: (keys: string[]) => void;
  /** Address the SFTP login name falls back to when no username is set. */
  email: string;
  /** Group path derived from the selected client; '' when none is selected. */
  groupPath: string;
  /**
   * Notice shown once SFTP is enabled. Creating an SFTP user and converting an
   * existing web user are meaningfully different — the latter destroys their MFA
   * enrolment — so the copy comes from the call site.
   */
  enableNotice: string;
  disabled?: boolean;
  /**
   * Set when the user is not eligible for SFTP at all (internal users, who are
   * cross-client). The toggle is disabled with an explanation rather than hidden,
   * so the rule is visible instead of surfacing as a failed save.
   *
   * A user who somehow already has SFTP stays editable so it can be turned off —
   * the goal is to prevent the state, not to strand anyone in it.
   */
  ineligibleReason?: string | null;
  /** Problem with enabling SFTP itself (ineligible user, no client); marks the toggle invalid. */
  toggleError?: string | null | undefined;
  /** Marks the SFTP Username input invalid and links the message. */
  usernameError?: string | null | undefined;
  /** Shown under the SSH key editor and linked to its group. */
  keysError?: string | null | undefined;
  /** Index of the specific saved key `keysError` is about, from `getSftpFieldErrors`. */
  invalidKeyIndex?: number | null | undefined;
}

export type SftpFieldErrors = Partial<Record<'sftpUsername' | 'sshPublicKeys', string>> & {
  /** Index into `sshPublicKeys` of the specific key that failed, when `sshPublicKeys` is set. */
  invalidKeyIndex?: number;
};

/**
 * Splits the shared SFTP validation into per-control messages so each error
 * lands on the control the user has to change. Also reports *which* saved key
 * failed, so the UI can point at that key's Remove control instead of
 * guessing at the first one.
 */
export function getSftpFieldErrors(fields: {
  sftpUsername?: string | null | undefined;
  sshPublicKeys?: readonly string[] | undefined;
}): SftpFieldErrors {
  const errors: SftpFieldErrors = {};
  const usernameError = getSftpFieldsError({ sftpUsername: fields.sftpUsername });
  if (usernameError) errors.sftpUsername = usernameError;

  const keys = fields.sshPublicKeys ?? [];
  for (const [index, key] of keys.entries()) {
    const keyError = getSshPublicKeyError(key);
    if (keyError) {
      errors.sshPublicKeys = `SSH public key ${index + 1}: ${keyError}`;
      errors.invalidKeyIndex = index;
      break;
    }
  }
  return errors;
}

/**
 * The SFTP access controls shared by the create-user modal and the user detail
 * edit form: the enable toggle, SFTP username, derived group path, and SSH key
 * editor. Both forms accept the same fields, so they render the same controls.
 *
 * Fully controlled — the parent owns the form state and decides what to submit.
 */
export const SftpAccessFields: React.FC<SftpAccessFieldsProps> = ({
  idPrefix,
  isSftp,
  onIsSftpChange,
  sftpUsername,
  onSftpUsernameChange,
  sshPublicKeys,
  onSshPublicKeysChange,
  email,
  groupPath,
  enableNotice,
  disabled,
  ineligibleReason,
  toggleError,
  usernameError,
  keysError,
  invalidKeyIndex,
}) => {
  const checkboxId = `${idPrefix}-isSftp`;
  const usernameId = `${idPrefix}-sftpUsername`;
  const toggleErrorId = `${idPrefix}-sftp-error`;
  const usernameHelpId = `${idPrefix}-sftpUsername-help`;
  const usernameErrorId = `${idPrefix}-sftpUsername-error`;
  const keysErrorId = `${idPrefix}-sshPublicKeys-error`;
  // The toggle error repeats the ineligibility reason for internal users; show it once.
  const showIneligibleReason = !!ineligibleReason && toggleError !== ineligibleReason;
  const usernameWarning = isSftp ? getSftpUsernameWarning(email, sftpUsername) : null;

  // Ineligible users can still switch SFTP off if they somehow have it on, so the
  // control only locks when it is already in the allowed (off) state.
  const lockToggle = !!ineligibleReason && !isSftp;

  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-2">
        <Checkbox
          id={checkboxId}
          checked={isSftp}
          onCheckedChange={(checked) => onIsSftpChange(checked === true)}
          disabled={disabled || lockToggle}
          aria-invalid={toggleError ? true : undefined}
          aria-describedby={toggleError ? toggleErrorId : undefined}
        />
        <Label
          htmlFor={checkboxId}
          className={`font-normal ${lockToggle ? 'text-muted-foreground' : 'cursor-pointer'}`}
        >
          <span className="font-medium">Enable SFTP</span>
        </Label>
      </div>

      {showIneligibleReason && <p className="text-xs text-muted-foreground">{ineligibleReason}</p>}
      {/* Stay mounted. Chrome moves focus when the aria-describedby node is removed. */}
      <p className={toggleError ? 'text-sm text-destructive' : 'hidden'} id={toggleErrorId}>
        {toggleError}
      </p>

      {isSftp && (
        <>
          <div className="ml-6 p-3 bg-amber-50 border border-amber-200 rounded-md">
            <div className="flex items-start gap-2">
              <IconAlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-amber-800">SFTP-only account</p>
                <p className="text-amber-700 mt-1">{enableNotice}</p>
              </div>
            </div>
          </div>

          <div className="ml-6">
            <Label htmlFor={usernameId} className="text-sm font-medium">
              SFTP Username
            </Label>
            <Input
              id={usernameId}
              value={sftpUsername}
              onChange={(e) => onSftpUsernameChange(e.target.value)}
              placeholder="e.g. jsmith"
              className="mt-1"
              disabled={disabled}
              aria-invalid={usernameError ? true : undefined}
              aria-describedby={
                usernameError ? `${usernameErrorId} ${usernameHelpId}` : usernameHelpId
              }
            />
            <p
              className={usernameError ? 'text-sm text-destructive mt-1' : 'hidden'}
              id={usernameErrorId}
            >
              {usernameError}
            </p>
            <p className="text-xs text-muted-foreground mt-1" id={usernameHelpId}>
              Custom login username for SFTP access. If left blank, the email address will be used.
              When set, use at least 3 characters (up to 255); only letters, numbers, and @ . _ -
              are accepted.
            </p>
            {usernameWarning && (
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">{usernameWarning}</p>
            )}
          </div>

          {groupPath ? (
            <div className="ml-6 p-2 bg-muted/50 rounded-md">
              <span className="text-xs text-muted-foreground">SFTP Group Path</span>
              <p className="font-mono text-sm mt-0.5">{groupPath}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Auto-generated from the selected client. The group will be created in Keycloak
                automatically if it doesn't exist.
              </p>
            </div>
          ) : (
            <p className="ml-6 text-xs text-amber-700 dark:text-amber-400">
              Select a client above to generate the SFTP group path.
            </p>
          )}

          <div className="ml-6">
            <SshPublicKeysEditor
              id={`${idPrefix}-sshPublicKeys`}
              value={sshPublicKeys}
              onChange={onSshPublicKeysChange}
              disabled={disabled ?? false}
              invalidKeyIndex={keysError ? invalidKeyIndex : undefined}
              errorId={keysError ? keysErrorId : undefined}
            />
            <p className={keysError ? 'text-sm text-destructive mt-1' : 'hidden'} id={keysErrorId}>
              {keysError}
            </p>
          </div>
        </>
      )}
    </div>
  );
};
